import {
  Alert,
  FormControl,
  formControlLabelClasses,
  styled,
  InputLabel,
  MenuItem,
  RadioGroup,
  Select,
  Typography,
  IconButton,
  FormControlLabel,
  Radio,
  TextField,
  FormHelperText,
} from "@mui/material";
import { MdClose } from "@react-icons/all-files/md/MdClose";
import { NAME_MISMATCH_REASON_OPTIONS } from "constants/nameMisMatchReasonOptions";
import React, { ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { IGenericInformation } from "store/slices/genericInformation";
import { SheetContainer } from "styles/common";
import { RedAsterisk } from "styles/controls";

const Container = styled("div")`
  padding: 8px 20px 16px 20px;
`;

const Header = styled("div")`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 20px 20px 0px 20px;
`;

const DrawerBody = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px 20px 20px 20px;
`;

const Group = styled(RadioGroup)`
  width: 100%;
  & .${formControlLabelClasses.root} {
    margin: 0px;
    color: #333333;
  }
  & > *:not(:last-child) {
    border-bottom: 1px solid lightgray;
  }
  & > * {
    padding: 8px;
  }
`;

interface NameMisMatchProps {
  reason: (text: string) => void;
}

function NameMismatch({ reason }: NameMisMatchProps) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<string>("");
  const [showError, setShowError] = useState<boolean>(false);
  const [showErrorOnMainField, setShowErrorOnMainField] =
    useState<boolean>(false);
  const [otherReasonText, setOtherReasonText] = useState<string>("");
  const { t } = useTranslation();
  const info = useSelector(
    (state: any) => state.info.allData
  ) as IGenericInformation;

  const clickHandler = () => {
    if (!value) {
      setShowErrorOnMainField(true);
    }
    setOpen(true);
  };
  const close = () => {
    setOpen(false);
  };

  const handleClose = () => {
    if (value === NAME_MISMATCH_REASON_OPTIONS[3] && !otherReasonText.trim()) {
      setShowError(true);
    } else {
      close();
    }
  };
  const handleChange = (_: ChangeEvent<HTMLInputElement>, value: string) => {
    setValue(value);
    reason(value);
    setShowErrorOnMainField(false);
    if (value === NAME_MISMATCH_REASON_OPTIONS[3]) {
      setOtherReasonText("");
      setShowError(false);
    } else {
      close();
    }
  };

  return (
    <>
      {info?.panDetails &&
        Number(info?.bankDetails?.userNameMatchScore) <
          Number(process.env.REACT_APP_NAME_MATCH_SCORE_LIMIT) && (
          <>
            <Alert
              severity="error"
              sx={{ ml: 2.25, mr: 2.25, color: "#333333" }}
            >
              {t("name_mismatch_error", {
                defaultValue: "Name mismatch detected",
              })}
            </Alert>
            <Container>
              <InputLabel>
                {t("reason_options", {
                  defaultValue: "Reason For Name Mismatch",
                })}
                <RedAsterisk>&thinsp;*</RedAsterisk>
              </InputLabel>
              <FormControl fullWidth id={"reason-for-edit"}>
                <div>
                  <Select
                    open={false}
                    value={value}
                    error={showErrorOnMainField}
                    onClick={clickHandler}
                    fullWidth
                    displayEmpty
                    renderValue={(value: any) => {
                      if (!value) {
                        return (
                          <Typography color="gray">
                            {t("select", { defaultValue: "Select" })}
                          </Typography>
                        );
                      }
                      return value;
                    }}
                  >
                    {NAME_MISMATCH_REASON_OPTIONS.map((item) => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </Select>
                  {showErrorOnMainField && (
                    <FormHelperText error>
                      {t("required_text", {
                        defaultValue: "This is required field",
                      })}
                    </FormHelperText>
                  )}
                </div>
              </FormControl>
            </Container>
          </>
        )}
      <SheetContainer open={open} anchor="bottom">
        <Header>
          <Typography variant="h6">
            {t("reason_options", { defaultValue: "Reason for Name Mismatch" })}
          </Typography>
          <IconButton id="close" onClick={handleClose}>
            <MdClose />
          </IconButton>
        </Header>
        <DrawerBody>
          <Group onChange={handleChange} value={value}>
            {NAME_MISMATCH_REASON_OPTIONS.map((item) => (
              <FormControlLabel
                key={item}
                value={item}
                control={<Radio />}
                label={item}
              />
            ))}
          </Group>
          {value === NAME_MISMATCH_REASON_OPTIONS[3] && (
            <div style={{ width: "100%" }}>
              <InputLabel>
                {t("enter_reason", {
                  defaultValue: "Enter Reason",
                })}
                <RedAsterisk>&thinsp;*</RedAsterisk>
              </InputLabel>
              <TextField
                type={"text"}
                value={otherReasonText}
                error={showError}
                helperText={
                  showError
                    ? t("required_text", {
                        defaultValue: "This is required field",
                      })
                    : ""
                }
                fullWidth={true}
                onChange={(e) => {
                  reason(e.target.value);
                  setOtherReasonText(e.target.value);
                  if (e.target.value.trim() === "") {
                    setShowError(true);
                  } else {
                    setShowError(false);
                  }
                }}
                autoFocus
                required
                slotProps={{
                  input: {
                    placeholder: t("reason", {
                      defaultValue: "Reason...",
                    }),
                  },
                }}
              />
            </div>
          )}
        </DrawerBody>
      </SheetContainer>
    </>
  );
}

export default NameMismatch;
